import React, { useState, useRef, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import Typed from 'typed.js';

// Animation keyframes
const pulseKeyframes = `
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fastPulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
`;

// Add keyframes to document
const style = document.createElement('style');
style.textContent = pulseKeyframes;
document.head.appendChild(style);

const TYPING_STRINGS = [
  "Hello! Ask our AI Assistant anything – it's basically a genius in disguise.",
  "Can you help with automation? We want to sit back and relax!",
  "We want to grow on social media. Can you make us internet famous?",
  "Need video content next week. Can you work magic that fast?",
  "What's the monthly cost of the AI service?",
  "Is there a free consultancy option? We love freebies!"
];

const RESPONSE_TYPES = {
  OPEN_TEXT: 'open_text',
  BOOLEAN: 'boolean',
  SERVICES: 'services',
  MULTIPLE_OPTIONS: 'multiple_options',
  BOOK_CALL: 'book_call',
};

const SUGGESTED_QUERIES = [
  {
    text: "Do you create content for international markets",
    type: "DEFAULT",
    icon:"chat.svg"
  },
  {
    text: "Do you have AI solutions for small teams",
    type: "DEFAULT",
    icon:"cog.svg"
  },
  {
    text: "Discover AI Automation",
    type: "FREE_OFFER",
    label: "FREE OFFER",
    icon:"meet.svg"
  },
  {
    text: "Do you have automation tools for social media?",
    type: "DEFAULT",
    icon:"tag.svg"
  },
  {
    text: "Book Now",
    type: "BOOK_A_CALL",
    label: "FREE EXPERT CALL",
    icon:"pie.svg"
  },
  {
    text: "Do you also provide paid marketing strategies?",
    type: "DEFAULT",
    icon:"tv.svg"
  }
];

const Options = ({ options = [], onSelect, type, selectedOptions = [], isMultiSelect }) => {
  const isSelected = (option) => selectedOptions.includes(option);

  const getImageName = (option) => {
    // Map options to specific image names in the public folder
    const imageMap = {
      "Design Services": "design.svg",
      "Web Development": "cog2.svg",
      "Content Production": "content.svg",
      "White-Label / Agency Partnership": "label.svg",
      "AI and Automation": "ai.svg",
      "Organic / Paid Marketing": "organic.svg",
    };
    return imageMap[option] || null;
  };

  if (type === RESPONSE_TYPES.BOOLEAN) {
    return (
      <div className="w-[100%] gap-5 flex justify-around items-center mt-4">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => onSelect(option)}
            className={`h-[50px]  flex-1 mr-2 inline-block rounded-md border transition-colors text-sm ${
              isSelected(option)
              ? 'border-[#E0FC2D] text-[#E0FC2D] '
              : 'border-[#cccccc] text-[#cccccc] hover:border-[#E0FC2D] hover:text-[#E0FC2D]'
            }`}
          >
            <span>{option}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row flex-wrap gap-4 mt-4">
      {options.map((option, index) => (
        <button
          key={index}
          onClick={() => onSelect(option)}
          className={`p-3 px-10 rounded-md border transition-colors text-sm ${
            isSelected(option)
              ? 'border-[#E0FC2D] text-[#E0FC2D] '
              : 'border-[#cccccc] text-[#cccccc] hover:border-[#E0FC2D] hover:text-[#E0FC2D]'
          }`}
        >
          {getImageName(option) && (
            <img
              src={`/${getImageName(option)}`}
              alt={option}
              className={`h-6 w-6 mr-2 inline-block`}
              style={{
                filter: isSelected(option)
                  ? 'invert(86%) sepia(25%) saturate(552%) hue-rotate(8deg) brightness(103%) contrast(106%)'
                  : 'invert(100%)',
              }}
            />
          )}
          {option}
        </button>
      ))}
    </div>
  );
};

const PulseIndicator = ({ isGenerating }) => (
  <div className="relative w-2 h-2 mr-2">
    <div className={`absolute inset-0 rounded-full ${
      isGenerating ? 'bg-white/50' : 'bg-current'
    }`} />
    
    {[1, 2, 3].map((index) => (
      <div
        key={index}
        className={`absolute inset-0 ${
          isGenerating 
            ? 'animate-[pulse-wave_1s_ease-in-out_infinite]' 
            : 'animate-[pulse-wave_2s_ease-in-out_infinite]'
        }`}
        style={{
          animationDelay: `${-index * 0.3}s`,
        }}
      >
        <div 
          className="absolute inset-0 rounded-full bg-current transform"
          style={{
            opacity: 0.75 - (index * 0.25),
          }}
        />
      </div>
    ))}
  </div>
);

const ChatInput = ({ 
  onSend, 
  isLoading, 
  messages, 
  currentResponseType, 
  selectedOptions = [], // Ensure default value
  onOptionSelect,
  options = [],
  onFocus,
  onBlur,
  isMessagesVisible
}) => {
  // State management
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // Refs
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const typedRef = useRef(null);

  const getInputValue = () => {
    if (!Array.isArray(selectedOptions)) {
      return message;
    }
    return selectedOptions.length > 0 ? selectedOptions.join(', ') : message;
  };

  // Initialize typing animation
  useEffect(() => {
    const typed = new Typed(inputRef.current, {
      strings: TYPING_STRINGS,
      typeSpeed: 50,
      backSpeed: 30,
      backDelay: 1000,
      loop: true,
      attr: 'placeholder',
      preStringTyped: () => {
        inputRef.current.placeholder = '';
      }
    });

    typedRef.current = typed;

    return () => {
      typed.destroy();
    };
  }, []);

  // Handle clicks outside suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && 
          !suggestionsRef.current.contains(event.target) &&
          inputRef.current && 
          !inputRef.current.contains(event.target)) {
        setIsFocused(false);
        onBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  // Get button text based on state
  const getButtonContent = () => {
    if (isLoading) return 'Generating';
    if (messages.length === 0) return 'Start AI Assistant';
    return 'Send';
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion.text);
    setIsFocused(false);
    onBlur();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setMessage(newValue);
    
    // If we have selected options, try to parse them from the input
    if (selectedOptions.length > 0) {
      const inputOptions = newValue.split(',').map(opt => opt.trim()).filter(opt => opt);
      
      // For boolean type, only keep the last option
      if (currentResponseType === RESPONSE_TYPES.BOOLEAN) {
        const lastOption = inputOptions[inputOptions.length - 1];
        if (lastOption && ['yes', 'no'].includes(lastOption.toLowerCase())) {
          onOptionSelect([lastOption.toLowerCase()]);
        }
      } else {
        // For other types, keep all valid options that are in the available options list
        const validOptions = inputOptions.filter(opt => 
          options.includes(opt) || selectedOptions.includes(opt)
        );
        onOptionSelect(validOptions);
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    const contentToSend = selectedOptions.length > 0 ? selectedOptions : message;
    const isValid = selectedOptions.length > 0 || message.trim().length >= 2;

    if (isValid) {
      onSend(contentToSend);
      setMessage('');
      setIsFocused(false);
      onBlur();
    }
  };


  const handleCloseSuggestions = (e) => {
    e.preventDefault(); // Prevent any default behavior
    setIsFocused(false);
    onBlur();
  };

  return (
    <div className="relative md:w-[914px] md:max-w-[914px]">
      {/* Suggestions Box */}
      <div 
        ref={suggestionsRef}
        className={`absolute bottom-full flex flex-col justify-center items-center 
          md:h-[380px] left-0 right-0 mb-4 bg-black border border-[#707070] 
          rounded-[20px] overflow-hidden shadow-lg transition-all duration-700 ease-in-out
          ${isFocused && messages.length === 0
            ? 'opacity-100 max-h-[380px] mt-[5%] transform translate-y-0' 
            : 'opacity-0 max-h-0 pointer-events-none transform -translate-y-full'
          }`}
      >
        {/* Suggestions Header */}
        <div className="flex justify-center px-[20px] items-center w-full">
          <div className="flex justify-between items-center w-full border-b border-zinc-800/50 py-[18px]">
            <p className="text-[#B5B5B5] text-[14px]">Ask our AI Assistant everything</p>
            <button 
              onClick={handleCloseSuggestions}
              className="cursor-pointer hover:opacity-75 transition-opacity"
            >
              <img src="/cross.svg" alt="close" />
            </button>
          </div>
        </div>

        {/* Suggestions Grid */}
        <div className="p-5 overflow-y-scroll md:overflow-y-hidden flex flex-wrap justify-center gap-4 items-center">
          {SUGGESTED_QUERIES.map((suggestion, index) => (
            <SuggestionButton
              key={index}
              suggestion={suggestion}
              onClick={handleSuggestionClick}
            />
          ))}
        </div>
      </div>

      {/* Input Form */}
      <form 
        onSubmit={handleSubmit} 
        className={`flex items-center gap-3 transition-all ${
          isFocused && window.innerWidth <= 768 ? 'fixed -bottom-[60px] left-0 w-full p-4 z-10' : ''
        } ${
          !isFocused && messages.length === 0 ? 'mt-[200px]' : !isMessagesVisible ? 'mt-[200px]' : ''
        }`}
      >
        <div className="flex-1 flex relative">
          <input
            ref={inputRef}
            type="text"
            value={getInputValue()} // Use the helper function here
            onChange={handleInputChange}
            onFocus={() => {
              setIsFocused(true);
              onFocus();
            }}
            onBlur={() => {
              if (!isFocused) {
                setIsFocused(false);
                onBlur();
              }
            }}
            className={`w-full h-[52px] md:h-[68.14px] pl-4 pr-[50px] md:pr-[150px] text-[14px] rounded-[100px] 
              bg-zinc-800/50 text-white border border-zinc-700/50 
              focus:border-[#E0FC2D] focus:outline-none text-sm 
              placeholder:text-white transition-all duration-700
              
              
              `}
          />

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || (selectedOptions.length === 0 && message.trim().length < 2)}
            className={`w-[40px] md:w-auto h-[40px] mt-[6px] md:mt-[14px] absolute hover:bg-none 
              md:px-6 right-2 md:right-3 rounded-[100px] font-medium text-sm border 
              flex items-center justify-center transition-all
              ${(!isLoading && (selectedOptions.length > 0 || message.trim().length >= 2))
                ? 'bg-[#E0FC2D] hover:border-[#E0FD2C] hover:bg-transparent hover:text-[#E0FC2D] text-black border-gray-800' 
                : 'bg-zinc-800 text-white/50 border-zinc-700/50'
              }`}
          >
            {/* Desktop Button Content */}
            <div className="md:flex hidden items-center gap-2">
              <PulseIndicator isGenerating={isLoading} />
              <span>{getButtonContent()}</span>
            </div>

            {/* Mobile Button Content */}
            <div className="flex md:hidden pr-1 items-center justify-center">
              <img src="/send.svg" alt="send" />
            </div>
          </button>
        </div>
      </form>
    </div>
  );
};

const ChatMessage = ({ bookCallUrl,message, isUser, type, options, onSelect, selectedOptions }) => {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 last:mb-0`}>
      <div className={`max-w-[100%] ${!isUser &&'w-[100%]'} rounded-2xl p-4 text-white `}>
        {isUser ? (
          <div className="text-[#E0FC2D] text-right text-xs uppercase tracking-wider mb-2"></div>
        ) : (
          <div className="text-[#E0FC2D] text-[12px] tracking-wider mb-2">MWmediaGBT</div>
        )}
        <div className={`whitespace-pre-wrap text-[14px] ${isUser ? 'text-right w-auto p-4 rounded-lg bg-[#FFFFFF1A]' : 'text-left w-[100%]'}`}>
          {message}
        </div>
        {!isUser && options && (
          <Options 
            options={options} 
            onSelect={onSelect} 
            type={type}
            selectedOptions={selectedOptions}
            isMultiSelect={type === RESPONSE_TYPES.SERVICES || type === RESPONSE_TYPES.MULTIPLE_OPTIONS}
          />
        )}
        {!isUser && type === RESPONSE_TYPES.BOOK_CALL && (
          <a 
            href={`${bookCallUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-[100%] h-[89px] flex justify-between items-center mt-4 px-4 rounded-xl border border-[#E0FC2D] text-[#E0FC2D] hover:bg-[#E0FC2D]/10 transition-colors text-sm text-center font-medium"
          >
            <div className='flex gap-[10px] justify-start items-center'>
              <img src="/video.svg" alt="" />
              <div className='text-left'>
                <p>Book a Call</p>
                <p>Book Free Expert Call</p>
              </div>
            </div>
            <img src="/videobg.svg" alt="" />
          </a>
        )}
      </div>
    </div>
  );
};

const SuggestionButton = ({ suggestion, onClick }) => {
  let buttonClass = "w-full md:max-w-[280px] h-[50px] md:h-[133px] rounded-md md:rounded-none text-left px-[10px] md:p-[20px] flex md:flex-col gap-2 justify-start md:justify-center items-center md:items-start text-left transition-colors text-[14px] hover:scale-[1.01] transform duration-200 ";
  
  if (suggestion.type === "BOOK_A_CALL" || suggestion.type === "FREE_OFFER") {
    buttonClass += "border border-[#E0FC2D] text-[#E0FC2D] hover:bg-[#E0FC2D]/10";
  } else {
    buttonClass += "border border-zinc-800/50 text-white hover:bg-zinc-800/50";
  }

  return (
    <button
      onClick={() => onClick(suggestion)}
      className={buttonClass}
    >
      <img src={suggestion.icon} alt="" className="w-4 h-4 md:w-6 md:h-6" />
      <div>
        {suggestion.type !== "DEFAULT" && (
          <div className="text-[8px] md:text-[10px] w-full text-left uppercase tracking-wider text-[#E0FC2D] mb-1">
            {suggestion.label}
          </div>
        )}
        <div className="text-[12px] md:text-[16px]">{suggestion.text}</div>
      </div>
    </button>
  );
};

const MetaManager = () => {
  useEffect(() => {
    // Create or update viewport meta tag
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    meta.content = 'width=device-width, initial-scale=1, maximum-scale=1';
  }, []);

  return null;
};

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentResponseType, setCurrentResponseType] = useState(RESPONSE_TYPES.OPEN_TEXT);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasInitialAnimationPlayed, setHasInitialAnimationPlayed] = useState(false);
  const [isMessagesVisible, setIsMessagesVisible] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messagesRef = useRef(null);
  const contentRef = useRef(null);

  // Play initial animation
  useEffect(() => {
    setTimeout(() => {
      setHasInitialAnimationPlayed(true);
    }, 100);
  }, []);

  // Create new thread effect
  useEffect(() => {
    const createNewThread = async () => {
      try {
        const response = await fetch('https://non-profit-org-rosy.vercel.app/v1/assistant/thread/create', {
          method: 'POST',
          headers: {
            'accept': 'application/json'
          }
        });
        const data = await response.json();
        localStorage.setItem('threadId', data.thread_id);
      } catch (error) {
        console.error('Error creating thread:', error);
      }
    };

    createNewThread();
  }, []);

  // Scroll to bottom effect
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight + 100;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleCloseMessages = () => {
    setIsMessagesVisible(false);
    setCurrentResponseType(RESPONSE_TYPES.OPEN_TEXT);
  };

  const handleOptionSelect = (options) => {
    // Ensure options is an array
    const optionsArray = Array.isArray(options) ? options : [options];
    
    if (currentResponseType === RESPONSE_TYPES.BOOLEAN) {
      // For boolean, only keep the last option
      setSelectedOptions([optionsArray[optionsArray.length - 1]]);
    } else {
      // For other types, allow multiple options
      setSelectedOptions(optionsArray);
    }
  };

  // Handle message sending
  const handleSend = async (message) => {
    const threadId = localStorage.getItem('threadId');
    if (!threadId) {
      console.error('No thread ID found');
      return;
    }
    setIsLoading(true);
    const newMessages = [...messages, { 
      content: Array.isArray(message) ? message.join(', ') : message, 
      isUser: true 
    }];
    setMessages(newMessages);
    setIsMessagesVisible(true);
    setSelectedOptions([]);

    try {
      // Check if it's the 5th message
      if (newMessages.length === 9) {
        setIsLoading(true);
        const bookCallResponse = {
          content: "I think I understand your needs better now. Would you like to schedule a call with one of our experts to discuss your project in detail?",
          type: RESPONSE_TYPES.BOOK_CALL,
          isUser: false,
          bookCallUrl: 'https://calendly.com/mw-media'
        };
        setMessages([...newMessages, bookCallResponse]);
        setCurrentResponseType(RESPONSE_TYPES.BOOK_CALL);
        setIsLoading(false);
        return;
      }

      const response = await fetch(`https://non-profit-org-rosy.vercel.app/v1/assistant/thread/${threadId}/chat`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: Array.isArray(message) ? message.join(', ') : message,
          stream: false
        })
      });

      const data = await response.json();
      
      if (data.success) {
        const responseMessage = {
          content: data.message.response,
          type: data.message.type,
          isUser: false
        };

        // Add options based on response type
        switch (data.message.type) {
          case RESPONSE_TYPES.BOOLEAN:
            responseMessage.options = data.message.values || ['yes', 'no'];
            break;
          case RESPONSE_TYPES.SERVICES:
            responseMessage.options = data.message.values || [
              'Design Services',
              'Web Development',
              'Content Production',
              'White-Label / Agency Partnership',
              'AI and Automation',
              'Organic / Paid Marketing'
            ];
            break;
          case RESPONSE_TYPES.MULTIPLE_OPTIONS:
            responseMessage.options = data.message.values || [];
            break;
          case RESPONSE_TYPES.BOOK_CALL:
            responseMessage.bookCallUrl = data.message.values || 'https://calendly.com/mw-media';
            break;
        }

        setMessages([...newMessages, responseMessage]);
        setCurrentResponseType(data.message.type);
        scrollToBottom();
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error getting response:', error);
      setMessages([
        ...newMessages,
        {
          content: "Sorry, I couldn't process your request. Please try again.",
          type: RESPONSE_TYPES.OPEN_TEXT,
          isUser: false
        }
      ]);
      setCurrentResponseType(RESPONSE_TYPES.OPEN_TEXT);
    }
    setSelectedOptions([]);
    setIsLoading(false);
  };

  return (

    <>
      <MetaManager/>
      <div ref={chatContainerRef} className="flex pt-[25px] flex-col min-h-screen bg-transparent ">
        <div className="flex-1 flex flex-col items-center">
          {/* Main content container */}
          <div 
            ref={contentRef}
            className="w-full flex flex-col items-center transition-all duration-700 ease-in-out"
            style={{
              transform: hasInitialAnimationPlayed ? 'translateY(0)' : 'translateY(100vh)'
            }}
          >
            {/* Messages section */}
            {messages.length > 0 && isMessagesVisible && (
              <div 
                ref={messagesRef}
                className="w-full md:w-[914px] md:max-w-[914px] border border-gray-800 rounded-2xl overflow-hidden mb-4 transition-all duration-700"
                style={{
                  transform: hasInitialAnimationPlayed ? 'translateY(0)' : 'translateY(-100%)',
                  opacity: hasInitialAnimationPlayed ? 1 : 0
                }}
              >
                <div className="flex justify-center px-[20px] items-center w-full">
                  <div className="flex justify-between items-center w-full py-[18px]">
                    <p className="text-[#B5B5B5] text-[14px]">Ask our AI Assistant everything</p>
                    <button 
                      onClick={handleCloseMessages}
                      className="cursor-pointer hover:opacity-75 transition-opacity"
                    >
                      <img src="/cross.svg" alt="close" />
                    </button>
                  </div>
                </div>

                <div className="absolute border-t border-zinc-800/50 top-[58px] left-0 right-0 h-16 bg-gradient-to-b from-black to-transparent pointer-events-none z-10" />
                
                <style>
                  {`
                    .custom-scrollbar::-webkit-scrollbar {
                      margin-top:20px;
                      width: 3px;
                      background: #474747;
                      border-radius: 3px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                      background: rgba(255, 255, 255, 0.7);
                      border-radius: 3px;
                    }
                    .custom-scrollbar:hover::-webkit-scrollbar-thumb {
                      background: rgba(255, 255, 255, 0.7);
                    }
                  `}
                </style>
                
                <div className="max-h-[400px] overflow-y-auto overflow-x-hidden mx-3 mt-[20px] mb-[20px] p-4 space-y-4 custom-scrollbar">
                  <div className="flex flex-col">
                    {messages.map((message, index) => (
                      <div key={index} className="animate-fadeIn">
                        <ChatMessage 
                          message={message.content}
                          isUser={message.isUser}
                          type={message.type}
                          options={message.options}
                          onSelect={handleOptionSelect}
                          selectedOptions={selectedOptions}
                          bookCallUrl={message.bookCallUrl}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div ref={messagesEndRef} className="messages-container" />
              </div>
            )}

            {/* Input section */}
            <div 
              className={`w-full md:w-[914px] md:max-w-[914px] transition-all duration-700 ease-in-out ${
                messages.length > 0 ? 'pt-4 border-t border-zinc-800/50' : ''

              }`}
              style={{
                // transform: `translateY(${isInputFocused && messages.length === 0 ? '380px' :  !isMessagesVisible ?'200px':'0'})`,
                transform: `translateY(${isInputFocused && messages.length === 0 ? '380px' :  '0'})`,
                transition: 'transform 0.7s ease-in-out'
              }}
            >
              <ChatInput 
                onSend={handleSend}
                isLoading={isLoading}
                messages={messages}
                currentResponseType={currentResponseType}
                selectedOptions={Array.isArray(selectedOptions) ? selectedOptions : []} // Ensure array
                onOptionSelect={handleOptionSelect}
                options={messages[messages.length - 1]?.options || []}
                onFocus={() => {
                  setIsInputFocused(true)
                  setIsMessagesVisible(true)
                }}
                isMessagesVisible={isMessagesVisible}
                onBlur={() => setIsInputFocused(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatInterface;